import * as React from 'react';
import {Alert, Button, InputNumber, Table} from 'antd';
import {ColumnProps} from 'antd/lib/table';

import {ErrorResponse, InventoryStock, InventoryStockItem} from '../api/dtos/dtos';
import locales from '../locales';
import ButtonBar from '../common/ButtonBar';
import {numberGroupFormatter, renderFixedPriceColumn} from '../utils/number-utils';
import * as tableUtils from "../utils/table-utils";
import {GridState, searchableColumn, searchableSortableColumn, simpleColumn} from "../utils/table-utils";
import {AppColors} from "../statistics/utils/colors";
import furoApi from "../api/FuroApi";
import {ErrorAlert} from "../common/ErrorAlert";
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from "../app/ApplicationContext";
import accountProvider from "../account/AccountProvider";

export interface InfiniteStockSelection {
  stock: InventoryStock;
  count: number;
}

interface Props {
  forSale?: boolean;
  showInfiniteStocks?: boolean;
  onSelect: (stockItems: InventoryStockItem[], infiniteSelections: InfiniteStockSelection[]) => void;
}

const FIELD_INVENTORY_NUMBER = "inventoryStock.inventoryNumber"
const FIELD_NAME = "inventoryStock.name"
const FIELD_BRAND = "inventoryStock.brand"
const FIELD_TYPE = "inventoryStock.type"
const FIELD_INFINITE_INVENTORY_NUMBER = "inventoryNumber"
const FIELD_INFINITE_NAME = "name"
const FIELD_INFINITE_BRAND = "brand"
const FIELD_INFINITE_TYPE = "type"
const FIELD_STATE = "state"

interface State {
  showInfiniteStocks: boolean;
  selectedRowKeys: string[];
  selectedItems: InventoryStockItem[];
  selectedInfiniteRowKeys: string[];
  selectedInfiniteStocks: InventoryStock[];
  infiniteRowValues: { [stockId: string]: number };
  validationMessage?: string;
  gridStateItems: GridState<InventoryStockItem>;
  gridStateInfiniteStocks: GridState<InventoryStock>;
  error?: ErrorResponse;
}

class InventoryStockSelector extends React.Component<Props, State> {
  public readonly loadingHandler?: LoadingOverlayHandler;

  public state: State = {
    selectedRowKeys: [],
    selectedItems: [],
    selectedInfiniteRowKeys: [],
    selectedInfiniteStocks: [],
    infiniteRowValues: {},
    showInfiniteStocks: true,
    validationMessage: undefined,
    gridStateItems: tableUtils.initialGridState(10),
    gridStateInfiniteStocks: tableUtils.initialGridState(10),
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      ...this.state,
      showInfiniteStocks: props.showInfiniteStocks ?? true,
    };
  }

  public componentDidMount() {
    this.loadInventoryStockItems();
    if (this.state.showInfiniteStocks) {
      this.loadInfiniteInventoryStocks();
    }
  }

  public render(): React.ReactNode {
    const {gridStateItems, gridStateInfiniteStocks, showInfiniteStocks} = this.state;

    const finiteColumns: Array<ColumnProps<InventoryStockItem>> = [
      searchableSortableColumn(locales.item_number, FIELD_INVENTORY_NUMBER, this.state.gridStateItems, item => item.inventoryStock?.inventoryNumber),
      searchableSortableColumn(locales.name, FIELD_NAME, this.state.gridStateItems, item => item.inventoryStock?.name),
      searchableSortableColumn(locales.brand, FIELD_BRAND, this.state.gridStateItems, item => item.inventoryStock?.brand),
      searchableSortableColumn(locales.type, FIELD_TYPE, this.state.gridStateItems, item => item.inventoryStock?.type),
    ]

    if (accountProvider.isAdmin()) {
      finiteColumns.push(searchableColumn(locales.state, FIELD_STATE, this.state.gridStateItems, (item: InventoryStockItem) => item.state?.userName ?? item.state?.warehouseName));
    }

    finiteColumns.push(
      simpleColumn(locales.initial_cost, this.renderFiniteInitialCost),
      simpleColumn(locales.profit_margin_percent, item => `${item.profitMargin}%`),
      simpleColumn(locales.unit_price, this.renderFiniteUnitPrice),
    );

    const infiniteColumns: Array<ColumnProps<InventoryStock>> = [
      searchableSortableColumn(locales.item_number, FIELD_INFINITE_INVENTORY_NUMBER, this.state.gridStateInfiniteStocks, stock => stock.inventoryNumber),
      searchableSortableColumn(locales.name, FIELD_INFINITE_NAME, this.state.gridStateInfiniteStocks, stock => stock.name),
      searchableSortableColumn(locales.brand, FIELD_INFINITE_BRAND, this.state.gridStateInfiniteStocks, stock => stock.brand),
      searchableSortableColumn(locales.type, FIELD_INFINITE_TYPE, this.state.gridStateInfiniteStocks, stock => stock.type),
      simpleColumn(locales.initial_cost, this.renderInfiniteInitialCost),
      simpleColumn(locales.profit_margin_percent, stock => `${stock.infiniteStockProfitMargin}%`),
      simpleColumn(locales.unit_price, this.renderInfiniteUnitPrice),
      simpleColumn(locales.amount, this.renderInfiniteCountInput, 160),
    ];

    const finiteRowSelection = {
      selectedRowKeys: this.state.selectedRowKeys,
      onChange: (selectedRowKeys: Array<string | number>, selectedRows: InventoryStockItem[]) => {
        this.setState(prevState => {
          const pageKeys = (prevState.gridStateItems.data || []).map(this.getRowKey);
          const retainedItems = prevState.selectedItems.filter(item => !pageKeys.includes(this.getRowKey(item)));
          return {
            selectedItems: [...retainedItems, ...selectedRows],
            selectedRowKeys: [
              ...prevState.selectedRowKeys.filter(key => !pageKeys.includes(key)),
              ...selectedRowKeys.map(String).filter(key => pageKeys.includes(key)),
            ],
          };
        });
      },
    };

    const infiniteRowSelection = {
      selectedRowKeys: this.state.selectedInfiniteRowKeys,
      onChange: (selectedRowKeys: Array<string | number>, selectedRows: InventoryStock[]) => {
        this.setState(prevState => {
          const pageKeys = (prevState.gridStateInfiniteStocks.data || []).map(this.getInfiniteRowKey);
          const retainedStocks = prevState.selectedInfiniteStocks.filter(stock => !pageKeys.includes(this.getInfiniteRowKey(stock)));
          return {
            selectedInfiniteStocks: [...retainedStocks, ...selectedRows],
            selectedInfiniteRowKeys: [
              ...prevState.selectedInfiniteRowKeys.filter(key => !pageKeys.includes(key)),
              ...selectedRowKeys.map(String).filter(key => pageKeys.includes(key)),
            ],
          };
        });
      },
    };

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <Alert
          type="info"
          showIcon={true}
          style={{marginBottom: 16}}
          message={locales.inventory_stock_selected_count(this.state.selectedRowKeys.length)}
        />

        <div style={{fontWeight: 600, marginBottom: 8}}>{locales.inventory_stock_items}</div>
        <Table
          style={{marginBottom: 16}}
          dataSource={gridStateItems.data || []}
          columns={finiteColumns}
          rowSelection={finiteRowSelection}
          rowKey={this.getRowKey}
          bordered={true}
          size="small"
          pagination={tableUtils.defaultPaginationConfig(gridStateItems)}
          onChange={tableUtils.handleOnGridChange(gridStateItems, this.onItemsGridChange)}
          scroll={{x: 800}}
        />

        {showInfiniteStocks &&
            <>
                <Alert
                    type="info"
                    showIcon={true}
                    style={{marginBottom: 16}}
                    message={locales.inventory_stock_selected_count(this.state.selectedInfiniteRowKeys.length)}
                />
                <div style={{fontWeight: 600, marginBottom: 8}}>{locales.infinite_stock}</div>
                <Table
                    dataSource={gridStateInfiniteStocks.data || []}
                    columns={infiniteColumns}
                    rowSelection={infiniteRowSelection}
                    rowKey={this.getInfiniteRowKey}
                    bordered={true}
                    size="small"
                    pagination={tableUtils.defaultPaginationConfig(gridStateInfiniteStocks)}
                    onChange={tableUtils.handleOnGridChange(gridStateInfiniteStocks, this.onInfiniteStocksGridChange)}
                    scroll={{x: 800}}
                />
            </>
        }


        {this.state.validationMessage && (
          <div style={{color: AppColors.RED, marginTop: 8, textAlign: 'right'}}>{this.state.validationMessage}</div>
        )}

        <ButtonBar style={{marginTop: 8}}>
          <Button
            size="small"
            type="primary"
            icon="plus"
            disabled={this.state.selectedItems.length < 1 && this.state.selectedInfiniteStocks.length < 1}
            onClick={this.onAddSelected}
          >
            {locales.add}
          </Button>
        </ButtonBar>
        <ErrorAlert style={{marginTop: 16}} error={this.state.error}/>

      </>
    );
  }

  private renderFiniteInitialCost = (item: InventoryStockItem): React.ReactNode => {
    return renderFixedPriceColumn(item.hufCost);
  }

  private renderFiniteUnitPrice = (item: InventoryStockItem): React.ReactNode => {
    return renderFixedPriceColumn(item.hufCost * (1 + item.profitMargin / 100));
  }

  private renderInfiniteInitialCost = (stock: InventoryStock): React.ReactNode => {
    return renderFixedPriceColumn(stock.infiniteStockCost);
  }

  private renderInfiniteUnitPrice = (stock: InventoryStock): React.ReactNode => {
    return renderFixedPriceColumn(stock.infiniteStockCost! * (1 + stock.infiniteStockProfitMargin! / 100));
  }

  private renderInfiniteCountInput = (stock: InventoryStock): React.ReactNode => {
    const key = this.getInfiniteRowKey(stock);
    const rowValue = this.state.infiniteRowValues[key];
    return (
      <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
        <InputNumber
          style={{width: '100%'}}
          min={0}
          precision={0}
          value={rowValue}
          placeholder={locales.amount}
          formatter={numberGroupFormatter}
          onChange={this.onInfiniteCountChange(stock)}
        />
        <span style={{whiteSpace: 'nowrap'}}>{locales.pcs}</span>
      </div>
    );
  };

  private onAddSelected = () => {
    const infiniteSelections: InfiniteStockSelection[] = [];

    for (const stock of this.state.selectedInfiniteStocks) {
      const key = this.getInfiniteRowKey(stock);
      const count = this.state.infiniteRowValues[key] || 0;

      if (count == null || count < 1) {
        this.setState({validationMessage: `${locales.infinite_stock}: ${locales.field_cant_be_empty(locales.amount)} (${stock.inventoryNumber})`});
        return;
      }

      infiniteSelections.push({stock, count});
    }

    this.props.onSelect(this.state.selectedItems, infiniteSelections);
    this.setState({
      selectedItems: [],
      selectedRowKeys: [],
      selectedInfiniteStocks: [],
      selectedInfiniteRowKeys: [],
      validationMessage: undefined,
    });
  };

  private onInfiniteCountChange = (stock: InventoryStock) => (count?: number) => {
    const key = this.getInfiniteRowKey(stock);
    this.setState((prevState) => {
      const value = count ?? 0;
      const shouldSelect = value >= 1;
      const isSelected = prevState.selectedInfiniteRowKeys.includes(key);

      let selectedInfiniteRowKeys = prevState.selectedInfiniteRowKeys;
      let selectedInfiniteStocks = prevState.selectedInfiniteStocks;

      if (shouldSelect && !isSelected) {
        selectedInfiniteRowKeys = [...prevState.selectedInfiniteRowKeys, key];
        selectedInfiniteStocks = [...prevState.selectedInfiniteStocks, stock];
      } else if (!shouldSelect && isSelected) {
        selectedInfiniteRowKeys = prevState.selectedInfiniteRowKeys.filter((rowKey) => rowKey !== key);
        selectedInfiniteStocks = prevState.selectedInfiniteStocks.filter((selectedStock) => this.getInfiniteRowKey(selectedStock) !== key);
      }

      return {
        infiniteRowValues: {
          ...prevState.infiniteRowValues,
          [key]: value,
        },
        selectedInfiniteRowKeys,
        selectedInfiniteStocks,
        validationMessage: undefined,
      };
    });
  };

  private getRowKey = (item: InventoryStockItem): string => {
    return String(item.id!!);
  };

  private getInfiniteRowKey = (stock: InventoryStock): string => {
    return String(stock.id || stock.inventoryNumber);
  };

  private onItemsGridChange = (newState: GridState<InventoryStockItem>) => {
    this.setState({gridStateItems: newState}, () => this.loadInventoryStockItems());
  };

  private onInfiniteStocksGridChange = (newState: GridState<InventoryStock>) => {
    this.setState({gridStateInfiniteStocks: newState}, () => this.loadInfiniteInventoryStocks());
  };


  private loadInventoryStockItems = () => {
    const request = tableUtils.toGridRequest(this.state.gridStateItems);
    const itemsPromise = this.props.forSale
      ? furoApi.inventoryStockItems.getForSale(request)
      : furoApi.inventoryStockItems.getAllByUser(request);

    const promise = itemsPromise
      .then((response) => {
        this.setState((prevState) => ({
          gridStateItems: {
            ...prevState.gridStateItems,
            ...tableUtils.toGridState(response),
          },
          error: undefined,
        }));
      })
      .catch((error: ErrorResponse) => {
        this.setState({error});
      });

    withLoadingOverlay(promise, this.loadingHandler);

  };

  private loadInfiniteInventoryStocks = () => {
    const request = tableUtils.toGridRequest(this.state.gridStateInfiniteStocks);
    const promise = furoApi.inventoryStocks.getInfinite(request)
      .then((response) => {
        this.setState((prevState) => ({
          gridStateInfiniteStocks: {
            ...prevState.gridStateInfiniteStocks,
            ...tableUtils.toGridState(response),
          },
          error: undefined,
        }));
      })
      .catch((error: ErrorResponse) => {
        this.setState({error});
      });

    withLoadingOverlay(promise, this.loadingHandler);
  };

}

export default InventoryStockSelector;



// WEBPACK FOOTER //
// ./src/maintenance/InventoryStockSelector.tsx